import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { api } from '../lib/api-client';
import { IconButton } from './IconButton';

/**
 * Bouton + modal pour générer un lien de partage lecture seule.
 * Affiché dans TenderDetail.
 */
export function ShareButton({ tenderId }: { tenderId: string }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(7);
  const [showPrices, setShowPrices] = useState(true);
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState<{ url: string; expiresAt: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<{ url: string; expiresAt: number }>(
        `/api/tenders/${tenderId}/share`,
        { expiresInDays: days, showPrices },
      );
      setLink(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }

  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  function close() {
    setOpen(false);
    setLink(null);
    setError(null);
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-2xs font-mono uppercase tracking-widest text-steel-300 hover:text-amber-500"
        title="Share read-only"
        aria-label="Share"
      >
        ⎘ share
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 bg-ink-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={close}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="tc-panel max-w-md w-full p-6"
          >
            <div className="tc-label mb-1">share</div>
            <h3 className="font-display text-lg tracking-tight mb-4">Read-only share link</h3>
            {link ? (
              <div>
                <p className="text-sm text-steel-300 mb-3">
                  Valid until <strong className="text-amber-500">{new Date(link.expiresAt).toLocaleString('fr-FR')}</strong>
                </p>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={link.url}
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                    className="tc-input text-2xs"
                  />
                  <IconButton
                    icon={copied ? Check : Copy}
                    label={copied ? 'Copié !' : 'Copier le lien'}
                    variant="primary"
                    onClick={copy}
                  />
                </div>
                <div className="mt-4 flex justify-end">
                  <button onClick={close} className="tc-btn-ghost">close</button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-steel-300 mb-5">
                  Generate a link giving anyone read-only access to the proposal.
                  Internal LLM justifications stay hidden.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="tc-label block mb-2">
                      expiration · <span className="text-amber-500">{days} days</span>
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={30}
                      value={days}
                      onChange={(e) => setDays(Number(e.target.value))}
                      className="w-full accent-amber-500"
                      aria-label="Days until expiration"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showPrices}
                      onChange={(e) => setShowPrices(e.target.checked)}
                    />
                    <span className="text-steel-300">Show prices (HT)</span>
                  </label>
                </div>
                {error && (
                  <div className="mt-4 border border-red-900/60 bg-red-900/20 text-red-300 px-3 py-2 text-2xs font-mono">
                    ERROR · {error}
                  </div>
                )}
                <div className="mt-5 flex gap-2 justify-end">
                  <button onClick={close} className="tc-btn-ghost">cancel</button>
                  <button onClick={create} disabled={loading} className="tc-btn-primary">
                    {loading ? 'generating...' : '→ generate'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
